import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  BarChart3, DollarSign, TrendingUp, Users, Calendar,
  RefreshCw, AlertTriangle, CheckCircle
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { format } from 'date-fns';
import { useApiClient } from '../hooks/useApiClient';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { ErrorMessage } from '../components/ui/ErrorMessage';
import { formatCurrency, formatPercentage } from '../utils/formatters';

interface ExpenseSummary {
  total_amount: number;
  total_count: number;
  average_amount: number;
  status_breakdown: Record<string, number>;
  current_month_total: number;
  previous_month_total: number;
  growth_rate: number;
}

interface ExpenseTrend {
  period: string;
  total_amount: number;
  expense_count: number;
  average_amount: number;
}

interface CategoryAnalysis {
  category_id: string;
  category_name: string;
  total_amount: number;
  expense_count: number;
  percentage: number;
}

interface KPIMetrics {
  current_month_total: number;
  previous_month_total: number;
  month_over_month_growth: number;
  year_to_date_total: number;
  pending_approvals_count: number;
  average_processing_time: number;
  total_expenses_count: number;
}

// Removed unused DashboardWidget interface

const Dashboard: React.FC = () => {
  const apiClient = useApiClient();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  
  // Dashboard data state
  const [summary, setSummary] = useState<ExpenseSummary | null>(null);
  const [trends, setTrends] = useState<ExpenseTrend[]>([]);
  const [categories, setCategories] = useState<CategoryAnalysis[]>([]);
  const [kpis, setKpis] = useState<KPIMetrics | null>(null);
  // Removed unused widgets/dateRange state

  const loadDashboardData = useCallback(async (showRefreshSpinner = false) => {
    try {
      if (showRefreshSpinner) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // Load all dashboard data in parallel
      const [summaryRes, trendsRes, categoriesRes, kpisRes] = await Promise.all([
        apiClient.get('/analytics/summary'),
        apiClient.get('/analytics/trends?period=monthly&months=6'),
        apiClient.get('/analytics/categories?limit=8'),
        apiClient.get('/analytics/kpis')
      ]);

      setSummary(summaryRes);
      setTrends(trendsRes);
      setCategories(categoriesRes);
      setKpis(kpisRes);

    } catch (err: any) {
      console.error('Error loading dashboard data:', err);
      setError(err.response?.data?.detail || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [apiClient]);

  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return; // guard StrictMode double-invoke
    loadedRef.current = true;
    loadDashboardData();
  }, [loadDashboardData]);

  const handleRefresh = () => {
    loadDashboardData(true);
  };

  // Chart colors
  const COLORS = ['#1e40af', '#0891b2', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#f97316'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <button
            onClick={handleRefresh}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Retry</span>
          </button>
        </div>
        <ErrorMessage message={error} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Comprehensive expense analytics and insights</p>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">This Month</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(kpis?.current_month_total || 0)}
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <DollarSign className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4">
            <span className={`text-sm ${
              (kpis?.month_over_month_growth || 0) >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {formatPercentage(kpis?.month_over_month_growth || 0)} from last month
            </span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Approvals</p>
              <p className="text-2xl font-bold text-gray-900">
                {kpis?.pending_approvals_count || 0}
              </p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-full">
              <Users className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
          <div className="mt-4">
            <span className="text-sm text-gray-600">
              Avg. {kpis?.average_processing_time || 0} days to process
            </span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Year to Date</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(kpis?.year_to_date_total || 0)}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4">
            <span className="text-sm text-gray-600">
              {kpis?.total_expenses_count || 0} expenses this month
            </span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Average Expense</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(summary?.average_amount || 0)}
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <BarChart3 className="h-6 w-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-4">
            <span className="text-sm text-gray-600">
              {summary?.total_count || 0} total expenses
            </span>
          </div>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Trends Chart */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Monthly Spending Trends</h3>
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <Calendar className="h-4 w-4" />
              <span>Last 6 months</span>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="period" 
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => format(new Date(value + '-01'), 'MMM yyyy')}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                />
                <Tooltip 
                  formatter={(value: number) => [formatCurrency(value), 'Amount']}
                  labelFormatter={(label) => format(new Date(label + '-01'), 'MMMM yyyy')}
                />
                <Area 
                  type="monotone" 
                  dataKey="total_amount" 
                  stroke="#1e40af" 
                  fill="#1e40af" 
                  fillOpacity={0.1}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Category Breakdown Pie Chart */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Spending by Category</h3>
            <div className="text-sm text-gray-500">Top 8 categories</div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={categories}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ category_name, percentage }) => 
                    percentage > 5 ? `${category_name} (${percentage.toFixed(1)}%)` : ''
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="total_amount"
                >
                  {categories.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Expense Count Trends */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Expense Volume Trends</h3>
            <div className="text-sm text-gray-500">Number of expenses</div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="period" 
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => format(new Date(value + '-01'), 'MMM')}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip 
                  formatter={(value: number) => [value, 'Expenses']}
                  labelFormatter={(label) => format(new Date(label + '-01'), 'MMMM yyyy')}
                />
                <Bar dataKey="expense_count" fill="#0891b2" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Categories Table */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Top Spending Categories</h3>
            <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
              View All
            </button>
          </div>
          <div className="space-y-3">
            {categories.slice(0, 6).map((category, index) => (
              <div key={category.category_id} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  ></div>
                  <span className="text-sm font-medium text-gray-900">
                    {category.category_name}
                  </span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">
                    {formatCurrency(category.total_amount)}
                  </div>
                  <div className="text-xs text-gray-500">
                    {category.expense_count} expenses
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Status Overview */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Expense Status Overview</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {summary?.status_breakdown && Object.entries(summary.status_breakdown).map(([status, count]) => {
              const getStatusIcon = (status: string) => {
                switch (status.toLowerCase()) {
                  case 'approved': return <CheckCircle className="h-5 w-5 text-green-600" />;
                  case 'pending_approval': return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
                  case 'rejected': return <AlertTriangle className="h-5 w-5 text-red-600" />;
                  default: return <BarChart3 className="h-5 w-5 text-gray-600" />;
                }
              };

              const getStatusColor = (status: string) => {
                switch (status.toLowerCase()) {
                  case 'approved': return 'text-green-600 bg-green-50';
                  case 'pending_approval': return 'text-yellow-600 bg-yellow-50';
                  case 'rejected': return 'text-red-600 bg-red-50';
                  default: return 'text-gray-600 bg-gray-50';
                }
              };

              return (
                <div key={status} className={`p-4 rounded-lg ${getStatusColor(status)}`}>
                  <div className="flex items-center space-x-3">
                    {getStatusIcon(status)}
                    <div>
                      <p className="text-sm font-medium capitalize">
                        {status.replace('_', ' ')}
                      </p>
                      <p className="text-lg font-bold">{count}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
